import { useMemo, useState } from 'react';
import type { AgingBucket, Aging, OffsetRequest, OpenItem, OpenItemsReport } from '../api';
import type { Dictionary, Locale } from '../i18n';
import { amount as money, date as written } from '../format';

/**
 * Rozrachunki z kontrahentami (E7-S4): kto ile jest winien i od kiedy.
 *
 * Pozycje stoją pogrupowane po kontrahencie, bo tak się o nich rozmawia („co z Kowalskim?"),
 * a wiekowanie liczy się z terminu płatności na wskazany dzień. Kompensata łączy należność
 * i zobowiązanie tego samego kontrahenta jednym zapisem — do wysokości mniejszej z nich.
 */

const BUCKETS: AgingBucket[] = ['NOT_DUE', 'DAYS_1_30', 'DAYS_31_60', 'DAYS_61_90', 'OVER_90'];

export function bucketTone(bucket: AgingBucket): string {
  switch (bucket) {
    case 'NOT_DUE':
      return 'chip st-ok';
    case 'DAYS_1_30':
      return 'chip st-info';
    case 'DAYS_31_60':
      return 'chip st-warn';
    default:
      return 'chip st-bad';
  }
}

/** Pozycje po kontrahencie, w kolejności pierwszego wystąpienia (lista z API jest już po nazwie). */
export function groupByCounterparty(items: OpenItem[]): { counterpartyId: string; name: string; items: OpenItem[] }[] {
  const groups = new Map<string, { counterpartyId: string; name: string; items: OpenItem[] }>();
  for (const item of items) {
    const group = groups.get(item.counterpartyId) ?? { counterpartyId: item.counterpartyId, name: item.counterpartyName ?? '—', items: [] };
    group.items.push(item);
    groups.set(item.counterpartyId, group);
  }
  return [...groups.values()];
}

function AgingRow({ label, aging, locale }: { label: string; aging: Aging; locale: Locale }) {
  return (
    <tr>
      <td>
        <b>{label}</b>
      </td>
      {BUCKETS.map((bucket) => (
        <td key={bucket} className="n mono">
          {money(aging.byBucket[bucket] ?? '0', locale)}
        </td>
      ))}
      <td className="n mono">
        <b>{money(aging.total, locale)}</b>
      </td>
    </tr>
  );
}

export function SettlementsView({
  report,
  t,
  locale,
  editable,
  asOf,
  onlyOpen,
  onAsOf,
  onOnlyOpen,
  onOffset,
}: {
  report: OpenItemsReport | null;
  t: Dictionary;
  locale: Locale;
  editable: boolean;
  asOf: string;
  onlyOpen: boolean;
  onAsOf: (value: string) => void;
  onOnlyOpen: (value: boolean) => void;
  onOffset: (offset: OffsetRequest) => Promise<void>;
}) {
  const [offset, setOffset] = useState<{ counterpartyId: string; receivableId: string; payableId: string; amount: string; date: string } | null>(null);
  const groups = useMemo(() => groupByCounterparty(report?.items ?? []), [report]);

  const openOf = (counterpartyId: string, side: 'DEBIT' | 'CREDIT') =>
    (report?.items ?? []).filter((item) => item.counterpartyId === counterpartyId && item.side === side && Number(item.remaining) !== 0);

  return (
    <div className="panel">
      <div className="hd">
        <div>
          <h4>{t.journal.settlements}</h4>
          <p>{t.journal.settlementsLead}</p>
        </div>
        <div className="sp">
          <label className="fld">
            <span>{t.journal.asOf}</span>
            <input className="inp w" type="date" value={asOf} aria-label={t.journal.asOf} onChange={(event) => onAsOf(event.target.value)} />
          </label>
          <label className="row" style={{ alignItems: 'center', gap: 8 }}>
            <input type="checkbox" checked={onlyOpen} onChange={(event) => onOnlyOpen(event.target.checked)} />
            <span>{t.journal.onlyOpen}</span>
          </label>
        </div>
      </div>

      {report && (
        <div className="tbl">
          <table>
            <thead>
              <tr>
                <th />
                {BUCKETS.map((bucket) => (
                  <th key={bucket} className="n">
                    {t.journal.bucket[bucket]}
                  </th>
                ))}
                <th className="n">{t.journal.total}</th>
              </tr>
            </thead>
            <tbody>
              <AgingRow label={t.journal.receivables} aging={report.receivables} locale={locale} />
              <AgingRow label={t.journal.payables} aging={report.payables} locale={locale} />
            </tbody>
          </table>
        </div>
      )}

      {report && report.items.length === 0 && <p className="muted">{t.journal.noItems}</p>}

      {offset && (
        <div className="panel">
          <div className="hd">
            <div>
              <h4>{t.journal.offset}</h4>
              <p>{t.journal.offsetHint}</p>
            </div>
          </div>
          <div className="row">
            <label className="fld" style={{ flex: 2, minWidth: 220 }}>
              <span>{t.journal.offsetReceivable}</span>
              <select className="inp" value={offset.receivableId} onChange={(event) => setOffset({ ...offset, receivableId: event.target.value })}>
                {openOf(offset.counterpartyId, 'DEBIT').map((item) => (
                  <option key={item.id} value={item.id}>
                    {item.documentNumber ?? item.account} · {money(item.remaining, locale)}
                  </option>
                ))}
              </select>
            </label>
            <label className="fld" style={{ flex: 2, minWidth: 220 }}>
              <span>{t.journal.offsetPayable}</span>
              <select className="inp" value={offset.payableId} onChange={(event) => setOffset({ ...offset, payableId: event.target.value })}>
                {openOf(offset.counterpartyId, 'CREDIT').map((item) => (
                  <option key={item.id} value={item.id}>
                    {item.documentNumber ?? item.account} · {money(item.remaining, locale)}
                  </option>
                ))}
              </select>
            </label>
            <label className="fld">
              <span>{t.journal.offsetAmount}</span>
              <input className="inp mono" style={{ maxWidth: 140 }} inputMode="decimal" value={offset.amount} onChange={(event) => setOffset({ ...offset, amount: event.target.value })} />
            </label>
            <label className="fld">
              <span>{t.journal.offsetDate}</span>
              <input className="inp" type="date" value={offset.date} onChange={(event) => setOffset({ ...offset, date: event.target.value })} />
            </label>
          </div>
          <div className="row">
            <button
              type="button"
              className="btn"
              disabled={!offset.receivableId || !offset.payableId || offset.amount.trim() === ''}
              onClick={() =>
                void onOffset({
                  receivableId: offset.receivableId,
                  payableId: offset.payableId,
                  amount: offset.amount.replace(',', '.').trim(),
                  date: offset.date,
                }).then(() => setOffset(null))
              }
            >
              {t.journal.offsetSave}
            </button>
            <button type="button" className="btn plain" onClick={() => setOffset(null)}>
              {t.journal.cancel}
            </button>
          </div>
        </div>
      )}

      {groups.length > 0 && (
        <div className="tbl">
          <table>
            <thead>
              <tr>
                <th className="wrap">{t.journal.document}</th>
                <th>{t.journal.account}</th>
                <th>{t.journal.documentDate}</th>
                <th>{t.journal.dueDate}</th>
                <th className="n">{t.journal.amount}</th>
                <th className="n">{t.journal.settled}</th>
                <th className="n">{t.journal.remaining}</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {groups.map((group) => {
                const canOffset = editable && openOf(group.counterpartyId, 'DEBIT').length > 0 && openOf(group.counterpartyId, 'CREDIT').length > 0;
                return [
                  <tr key={`h-${group.counterpartyId}`}>
                    <td colSpan={7}>
                      <b>{group.name}</b>
                    </td>
                    <td className="act">
                      {canOffset && (
                        <button
                          type="button"
                          className="btn ghost sm"
                          onClick={() =>
                            setOffset({
                              counterpartyId: group.counterpartyId,
                              receivableId: openOf(group.counterpartyId, 'DEBIT')[0]!.id,
                              payableId: openOf(group.counterpartyId, 'CREDIT')[0]!.id,
                              amount: '',
                              date: asOf,
                            })
                          }
                        >
                          {t.journal.offset}
                        </button>
                      )}
                    </td>
                  </tr>,
                  ...group.items.map((item) => (
                    <tr key={item.id}>
                      <td className="wrap">
                        {item.documentNumber ?? '—'}{' '}
                        <span className="hint">{item.side === 'DEBIT' ? t.journal.receivableLabel : t.journal.payableLabel}</span>
                      </td>
                      <td className="mono">{item.account}</td>
                      <td>{written(item.documentDate, locale)}</td>
                      <td>{item.dueDate ? written(item.dueDate, locale) : '—'}</td>
                      <td className="n mono">{money(item.amount, locale)}</td>
                      <td className="n mono">{money(item.settled, locale)}</td>
                      <td className="n mono">
                        <b>{money(item.remaining, locale)}</b>
                      </td>
                      <td>
                        {Number(item.remaining) !== 0 && (
                          <span className={bucketTone(item.bucket)}>
                            {item.daysOverdue > 0 ? t.journal.overdue(item.daysOverdue) : t.journal.bucket.NOT_DUE}
                          </span>
                        )}
                      </td>
                    </tr>
                  )),
                ];
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
